import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Braces, CheckCircle2, DatabaseBackup, FileJson, RefreshCw, Save, SlidersHorizontal, Trash2, Upload } from 'lucide-react';
import { ConfigurationBackupContentDto, ConfigurationBackupDto, ConfigurationPreviewDto, ConfigurationTemplateDto, MeshDeviceDto } from '../../models';
import {
  applyMeshCentralConfiguration,
  backupMeshCentralConfiguration,
  createConfigurationTemplate,
  deleteConfigurationTemplate,
  getApiError,
  getConfigurationTemplate,
  getConfigurationTemplates,
  getMeshCentralConfigurationBackup,
  getMeshCentralConfigurationBackups,
  getMeshCentralLiveConfiguration,
  previewMeshCentralConfiguration,
} from '../../services/api';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { MeshHardwareConfigForm } from './MeshHardwareConfigForm';

interface Props { device: MeshDeviceDto }

export const MeshConfigurationEditor = ({ device }: Props) => {
  const [editorMode, setEditorMode] = useState<'hardware' | 'json'>('hardware');
  const [editor, setEditor] = useState('');
  const [sourceHash, setSourceHash] = useState('');
  const [templates, setTemplates] = useState<ConfigurationTemplateDto[]>([]);
  const [backups, setBackups] = useState<ConfigurationBackupDto[]>([]);
  const [viewedBackup, setViewedBackup] = useState<ConfigurationBackupContentDto>();
  const [preview, setPreview] = useState<ConfigurationPreviewDto>();
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const localValidation = useMemo(() => {
    if (!editor.trim()) return { valid: false, message: 'Конфиг ещё не загружен' };
    try {
      const parsed = JSON.parse(editor);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? { valid: true, message: 'JSON синтаксически валиден' }
        : { valid: false, message: 'Корень JSON должен быть объектом' };
    } catch (caught) {
      return { valid: false, message: `JSON невалиден: ${caught instanceof Error ? caught.message : 'проверьте скобки и запятые'}` };
    }
  }, [editor]);

  const loadLists = useCallback(async () => {
    const [templateResponse, backupResponse] = await Promise.all([
      getConfigurationTemplates(),
      getMeshCentralConfigurationBackups(device.nodeId),
    ]);
    setTemplates(templateResponse.data.data);
    setBackups(backupResponse.data.data);
  }, [device.nodeId]);

  const loadLive = useCallback(async () => {
    setBusy(true); setError(''); setSuccess(''); setPreview(undefined); setAcknowledged(false);
    try {
      const response = (await getMeshCentralLiveConfiguration(device.nodeId)).data.data;
      setEditor(JSON.stringify(response.content, null, 2));
      setSourceHash(response.hash);
      await loadLists();
      setSuccess('Конфиг получен с терминала. Все значения показаны без маскировки.');
    } catch (caught) { setError(getApiError(caught)); }
    finally { setBusy(false); }
  }, [device.nodeId, loadLists]);

  useEffect(() => { if (device.online) void loadLive(); }, [device.online, loadLive]);

  const checkChanges = async () => {
    if (!localValidation.valid) return setError(localValidation.message);
    setBusy(true); setError(''); setSuccess(''); setAcknowledged(false);
    try { setPreview((await previewMeshCentralConfiguration({ nodeId: device.nodeId, content: editor })).data.data); }
    catch (caught) { setError(getApiError(caught)); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      await applyMeshCentralConfiguration({ nodeId: device.nodeId, content: editor, expectedHash: sourceHash,
        reason, acknowledgeCritical: acknowledged });
      setSuccess('Конфиг применён, Medscreener перезапущен, hash проверен.');
      await loadLive();
    } catch (caught) { setError(getApiError(caught)); }
    finally { setBusy(false); }
  };

  const selectTemplate = async (templateId: string) => {
    if (!templateId) return;
    setBusy(true); setError(''); setPreview(undefined); setAcknowledged(false);
    try {
      const item = (await getConfigurationTemplate(Number(templateId))).data.data;
      setEditor(JSON.stringify(item.content, null, 2));
      setSuccess(`Шаблон «${item.name}» загружен в редактор. На терминал он ещё не записан.`);
    } catch (caught) { setError(getApiError(caught)); }
    finally { setBusy(false); }
  };

  const uploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const content = await file.text();
      JSON.parse(content);
      setEditor(JSON.stringify(JSON.parse(content), null, 2));
      setTemplateName(file.name.replace(/\.json$/i, ''));
      setShowTemplateForm(true);
      setPreview(undefined); setAcknowledged(false); setError('');
    } catch (caught) { setError(`Файл не загружен: ${caught instanceof Error ? caught.message : 'невалидный JSON'}`); }
  };

  const saveTemplate = async () => {
    if (!templateName.trim() || !localValidation.valid) return;
    setBusy(true); setError('');
    try {
      await createConfigurationTemplate({ name: templateName, description: templateDescription, content: editor });
      await loadLists();
      setShowTemplateForm(false); setTemplateName(''); setTemplateDescription('');
      setSuccess('Полный шаблон сохранён.');
    } catch (caught) { setError(getApiError(caught)); }
    finally { setBusy(false); }
  };

  const removeTemplate = async (id: number) => {
    setBusy(true); setError('');
    try { await deleteConfigurationTemplate(id); await loadLists(); }
    catch (caught) { setError(getApiError(caught)); }
    finally { setBusy(false); }
  };

  const createBackup = async () => {
    setBusy(true); setError('');
    try { await backupMeshCentralConfiguration(device.nodeId); await loadLists(); setSuccess('Резервная копия сохранена.'); }
    catch (caught) { setError(getApiError(caught)); }
    finally { setBusy(false); }
  };

  const openBackup = async (id: number) => {
    setBusy(true); setError('');
    try { setViewedBackup((await getMeshCentralConfigurationBackup(device.nodeId, id)).data.data); }
    catch (caught) { setError(getApiError(caught)); }
    finally { setBusy(false); }
  };

  if (!device.online) return <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-900">Конфиг можно читать и менять, когда терминал online в MeshCentral.</div>;

  return <div className="space-y-4">
    <div aria-live="polite" className="space-y-2">
      {error && <div role="alert" className="rounded-xl border border-destructive/25 bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>}
      {success && <div className="rounded-xl border border-success/25 bg-success/10 px-4 py-2 text-sm text-success">{success}</div>}
    </div>

    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-2 shadow-sm">
      <div className="flex rounded-xl bg-muted/70 p-1" role="tablist" aria-label="Режим редактора конфига">
        <ModeButton active={editorMode === 'hardware'} onClick={() => setEditorMode('hardware')} icon={SlidersHorizontal} label="Оборудование" />
        <ModeButton active={editorMode === 'json'} onClick={() => setEditorMode('json')} icon={Braces} label="Ручной JSON" />
      </div>
      <p className="px-2 text-xs text-muted-foreground">Режимы синхронизированы: изменение в одном сразу видно в другом.</p>
    </div>

    <div className="grid min-h-0 gap-4 xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,0.8fr)]">
      <section className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div><h3 className="font-semibold">{editorMode === 'hardware' ? 'Оборудование терминала' : 'appsettings.json'}</h3><p className="text-xs text-muted-foreground">{editorMode === 'hardware' ? 'Выбор меняет нужные поля в JSON, но не записывает их до подтверждения.' : 'Секреты, ключи и PIN видны сразу. Не копируйте их в чаты.'}</p></div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 text-sm font-semibold hover:bg-muted focus-within:ring-2 focus-within:ring-ring"><Upload className="h-4 w-4" />JSON-файл<input type="file" accept="application/json,.json" className="sr-only" onChange={uploadFile} /></label>
            <Button variant="outline" onClick={loadLive} disabled={busy}><RefreshCw className={cn('h-4 w-4', busy && 'animate-spin')} />С терминала</Button>
          </div>
        </header>
        {editorMode === 'hardware'
          ? <div className="max-h-[590px] overflow-y-auto bg-muted/20"><MeshHardwareConfigForm value={editor} onChange={(next) => { setEditor(next); setPreview(undefined); setAcknowledged(false); }} onError={setError} /></div>
          : <textarea aria-label="Полный JSON конфига" spellCheck={false} value={editor} onChange={(event) => { setEditor(event.target.value); setPreview(undefined); setAcknowledged(false); }} className="h-[590px] w-full resize-none bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100 outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-400" />}
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
          <span className={cn('inline-flex items-center gap-2 text-xs font-semibold', localValidation.valid ? 'text-success' : 'text-destructive')}>
            {localValidation.valid ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{localValidation.message}
          </span>
          <Button onClick={checkChanges} disabled={busy || !localValidation.valid}><FileJson className="h-4 w-4" />Проверить изменения</Button>
        </footer>
      </section>

      <aside className="space-y-4">
        <section className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between"><div><h3 className="font-semibold">Шаблоны</h3><p className="text-xs text-muted-foreground">Хранят полный JSON.</p></div><Button size="sm" variant="outline" onClick={() => setShowTemplateForm((value) => !value)}><Save className="h-4 w-4" />Сохранить</Button></div>
          <select aria-label="Выбрать шаблон" defaultValue="" onChange={(event) => void selectTemplate(event.target.value)} className="mt-3 h-11 w-full rounded-xl border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring">
            <option value="">Выбрать шаблон…</option>{templates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          {showTemplateForm && <div className="mt-3 space-y-2 rounded-xl bg-muted/50 p-3">
            <input aria-label="Название шаблона" value={templateName} onChange={(event) => setTemplateName(event.target.value)} placeholder="Название шаблона" className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm" />
            <input aria-label="Описание шаблона" value={templateDescription} onChange={(event) => setTemplateDescription(event.target.value)} placeholder="Камера, тонометр, принтер" className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm" />
            <Button size="sm" className="w-full" onClick={saveTemplate} disabled={busy || !templateName.trim() || !localValidation.valid}>Сохранить текущий JSON</Button>
          </div>}
          {templates.length > 0 && <div className="mt-3 max-h-32 space-y-1 overflow-auto">{templates.map((item) => <div key={item.id} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1 text-xs hover:bg-muted"><span className="truncate">{item.name} · {item.size} B</span><button aria-label={`Удалить ${item.name}`} onClick={() => void removeTemplate(item.id)} className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button></div>)}</div>}
        </section>

        <section className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between"><div><h3 className="font-semibold">Резервные копии</h3><p className="text-xs text-muted-foreground">Полные, без маскировки.</p></div><Button size="sm" variant="outline" onClick={createBackup} disabled={busy}><DatabaseBackup className="h-4 w-4" />Backup</Button></div>
          <div className="mt-3 max-h-40 space-y-2 overflow-auto">{backups.length === 0 ? <p className="text-xs text-muted-foreground">Копий пока нет.</p> : backups.map((item) => <button key={item.id} onClick={() => void openBackup(item.id)} className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2 text-left text-xs hover:bg-muted"><span>#{item.id} · {new Date(item.createdAt).toLocaleString('ru-RU')}</span><span className="font-mono text-[10px] text-muted-foreground">{item.hash.slice(0, 8)}</span></button>)}</div>
        </section>
      </aside>
    </div>

    {preview && <section className={cn('rounded-2xl border p-4', preview.criticalChanges.length ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50')}>
      <div className="flex items-start gap-3"><AlertTriangle className={cn('mt-0.5 h-5 w-5 shrink-0', preview.criticalChanges.length ? 'text-amber-700' : 'text-emerald-700')} /><div><h3 className="font-semibold">Будет изменено: {preview.changes.length} полей</h3><p className="text-xs text-muted-foreground">Важных полей: {preview.criticalChanges.length}. Запрета на перезапись нет.</p></div></div>
      {preview.validation.errors.length > 0 && <ValidationList title="Ошибки" items={preview.validation.errors} danger />}
      {preview.validation.warnings.length > 0 && <ValidationList title="Предупреждения" items={preview.validation.warnings} />}
      <div className="mt-3 max-h-56 overflow-auto rounded-xl border border-black/10 bg-white/80">{preview.changes.map((item) => <div key={item.path} className={cn('grid gap-1 border-b border-black/5 px-3 py-2 font-mono text-[11px] last:border-0 md:grid-cols-[220px_1fr_1fr]', preview.criticalChanges.some((critical) => critical.path === item.path) && 'bg-amber-100/70')}><strong className="break-all">{item.path}</strong><span className="break-all text-rose-700">− {formatValue(item.before)}</span><span className="break-all text-emerald-700">+ {formatValue(item.after)}</span></div>)}</div>
      <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto]">
        <div className="space-y-2"><input aria-label="Причина изменения" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Причина изменения конфига" className="h-11 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
          {preview.criticalChanges.length > 0 && <label className="flex cursor-pointer items-start gap-2 text-xs"><input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" /><span>Я вижу и разрешаю изменение TerminalId, API/S3/STOMP, PIN и других индивидуальных данных.</span></label>}</div>
        <Button onClick={apply} disabled={busy || !preview.validation.valid || reason.trim().length < 5 || (preview.criticalChanges.length > 0 && !acknowledged)} className="min-h-11 self-start bg-amber-600 hover:bg-amber-700"><Save className="h-4 w-4" />Применить и перезапустить</Button>
      </div>
    </section>}

    {viewedBackup && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/70 p-4" role="dialog" aria-modal="true" aria-label={`Backup ${viewedBackup.id}`} onMouseDown={(event) => { if (event.target === event.currentTarget) setViewedBackup(undefined); }}><div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-card shadow-2xl"><header className="flex items-center justify-between border-b border-border px-4 py-3"><div><h3 className="font-semibold">Backup #{viewedBackup.id}</h3><p className="text-xs text-destructive">Содержит секреты без маскировки</p></div><Button variant="outline" onClick={() => setViewedBackup(undefined)}>Закрыть</Button></header><pre className="overflow-auto bg-slate-950 p-5 font-mono text-xs leading-5 text-slate-100">{JSON.stringify(viewedBackup.content, null, 2)}</pre></div></div>}
  </div>;
};

const ValidationList = ({ title, items, danger = false }: { title: string; items: string[]; danger?: boolean }) => <div className={cn('mt-3 rounded-xl border px-3 py-2 text-xs', danger ? 'border-destructive/30 bg-destructive/10 text-destructive' : 'border-amber-300 bg-amber-100/70 text-amber-900')}><strong>{title}:</strong><ul className="mt-1 list-disc space-y-1 pl-5">{items.map((item) => <li key={item}>{item}</li>)}</ul></div>;
const ModeButton = ({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof Braces; label: string }) => <button type="button" role="tab" aria-selected={active} onClick={onClick} className={cn('inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', active ? 'bg-white text-cyan-800 shadow-sm' : 'text-muted-foreground hover:bg-white/60 hover:text-foreground')}><Icon className="h-4 w-4" />{label}</button>;
const formatValue = (value: unknown) => value === undefined ? 'нет поля' : JSON.stringify(value);
